// Presets (§11.6): built-in and user looks; apply to the selection (one undo step per photo + a global
// undo toast), save the current look with chosen groups, import/export `.epbpreset.json`.
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLang, useT } from '../../i18n';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { useClipboard } from '../../state/clipboard';
import {
  BUILT_IN_PRESETS,
  applyPreset,
  deleteUserPreset,
  listUserPresets,
  makePreset,
  presetFromFile,
  presetToFile,
  saveUserPreset,
  PRESET_FILE_SUFFIX,
} from '../../features/presets';
import { GroupChecklist } from '../dialogs/SyncDialog';
import { DEFAULT_SYNC_GROUPS } from '../../model/defaults';
import { saveAs } from '../../io/fs';
import { sanitizeName } from '../../io/rename';
import type { Preset, SyncGroup } from '../../model/types';
import { importXmp, type XmpReport } from '../../features/xmpImport';
import { errorKey } from '../../i18n/errors';

export function PresetsPanel() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const [user, setUser] = useState<Preset[]>([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [groups, setGroups] = useState<SyncGroup[]>([...DEFAULT_SYNC_GROUPS]);
  const [msg, setMsg] = useState<string | null>(null);
  const [report, setReport] = useState<XmpReport | null>(null);
  const refresh = () => void listUserPresets().then(setUser);
  useEffect(refresh, []);

  const apply = (p: Preset) => {
    const lib = useLibrary.getState();
    const ids = lib.selected.size ? [...lib.selected] : lib.activeId ? [lib.activeId] : [];
    if (!ids.length) return;
    const before = useEditor.getState().updateMany(ids, (s) => applyPreset(s, p));
    useClipboard
      .getState()
      .setToast({ key: 'presetApplied', vars: { n: ids.length }, undo: { n: ids.length, before } });
  };
  const save = async () => {
    const id = useLibrary.getState().activeId;
    if (!id || !groups.length) return;
    await saveUserPreset(makePreset(name, groups, useEditor.getState().get(id)));
    setSaving(false);
    setName('');
    refresh();
  };
  const importFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,' + PRESET_FILE_SUFFIX;
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        await saveUserPreset(presetFromFile(await f.text()));
        setMsg(null);
        refresh();
      } catch (e) {
        setMsg(t('presetImportFailed', { reason: t(errorKey(e)) }));
      }
    };
    input.click();
  };
  // Lightroom `.xmp` (§11.6): mapped approximately; the report says what was and was not applied.
  const importXmpFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xmp';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        const { preset, report: r } = importXmp(await f.text(), f.name);
        await saveUserPreset(preset);
        setReport(r);
        setMsg(null);
        refresh();
      } catch (e) {
        setReport(null);
        setMsg(t('presetImportFailed', { reason: t(errorKey(e)) }));
      }
    };
    input.click();
  };
  const label = (p: Preset) => (lang === 'es' && p.nameEs ? p.nameEs : p.name);
  const row = (p: Preset) => (
    <li key={p.id} style={{ display: 'flex', gap: 4, alignItems: 'center', margin: '3px 0' }}>
      <button
        style={{ flex: 1, textAlign: 'left' }}
        onClick={() => apply(p)}
        title={t('applyPreset')}
        data-testid="preset-apply"
      >
        {label(p)}
        {p.builtIn && <span style={{ color: 'var(--muted)', fontSize: 11 }}> · {t('builtIn')}</span>}
      </button>
      {!p.builtIn && (
        <>
          <button
            title={t('exportPresetFile')}
            aria-label={`${t('exportPresetFile')}: ${label(p)}`}
            onClick={() =>
              void saveAs(
                new TextEncoder().encode(presetToFile(p)),
                `${sanitizeName(p.name) || 'preset'}${PRESET_FILE_SUFFIX}`,
                'application/json',
              )
            }
          >
            ⤓
          </button>
          <button
            title={t('deletePreset')}
            aria-label={`${t('deletePreset')}: ${label(p)}`}
            onClick={() => void deleteUserPreset(p.id).then(refresh)}
          >
            <X className="icon" aria-hidden="true" />
          </button>
        </>
      )}
    </li>
  );
  return (
    <div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {BUILT_IN_PRESETS.map(row)}
        {user.map(row)}
        {!BUILT_IN_PRESETS.length && !user.length && (
          <li style={{ color: 'var(--muted)' }}>{t('noPresets')}</li>
        )}
      </ul>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={() => setSaving(!saving)} data-testid="preset-save-open">
          {t('savePreset')}
        </button>
        <button onClick={importFile}>{t('importPreset')}</button>
        <button onClick={importXmpFile} data-testid="xmp-import">
          {t('importXmp')}
        </button>
      </div>
      {report && (
        <div data-testid="xmp-report" style={{ fontSize: 12, marginTop: 8 }}>
          <strong>{t('xmpReportTitle', { name: report.name })}</strong>
          <div style={{ opacity: 0.8, margin: '4px 0' }}>{t('xmpApprox')}</div>
          <div>{t('xmpMapped', { n: report.mapped.length })}</div>
          <ul style={{ margin: '2px 0 4px 16px', padding: 0 }}>
            {report.mapped.map((m) => (
              <li key={m.key}>
                {m.key} = {m.points ? t('xmpPoints', { n: m.points }) : String(m.value)} → {m.to}
              </li>
            ))}
          </ul>
          <div>{t('xmpIgnored', { n: report.ignored.length })}</div>
          <ul style={{ margin: '2px 0 4px 16px', padding: 0 }}>
            {report.ignored.map((m) => (
              <li key={m.key}>
                {m.key}: {t(m.reasonKey, m.vars)}
              </li>
            ))}
          </ul>
          <button onClick={() => setReport(null)}>{t('close')}</button>
        </div>
      )}
      {saving && (
        <div style={{ marginTop: 8 }}>
          <input
            placeholder={t('presetName')}
            aria-label={t('presetName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%',
              marginBottom: 6,
              background: 'var(--bg)',
              color: 'var(--text)',
              border: '1px solid var(--line)',
              borderRadius: 3,
              padding: 3,
            }}
            data-testid="preset-name"
          />
          <GroupChecklist value={groups} onChange={setGroups} />
          <button
            style={{ marginTop: 6, background: 'var(--accent-fill)', color: 'var(--on-accent)' }}
            onClick={() => void save()}
            data-testid="preset-save"
          >
            {t('ok')}
          </button>
        </div>
      )}
      {msg && <p style={{ color: 'var(--warn)', fontSize: 12 }}>{msg}</p>}
    </div>
  );
}
